import { useMemo, useState } from 'react';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Heading,
  List,
  ListIcon,
  ListItem,
  Tag,
  TagLabel,
  Text,
  useToken,
} from '@chakra-ui/react';
import { TagIcon } from '@udacity/chakra-uds-icons';

import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { Program } from '~/features/program/models/program';
import { isNanodegreeProgram } from '~/features/program/utils/is-program-type';
import { RAW_DURATION_OPTIONS, useHumanizeDuration } from '~/hooks/use-humanize-duration';

import { useExperimentalSegmentEvents } from '../hooks';
import {
  BookmarkIcon,
  BooksIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ICON_SIZE,
  OpenBookIcon,
  StopWatchIcon,
} from '../icons';
import { pluralize } from '../utils';

export type ProgramOutlineSectionProps = StaticProgramOutlineSectionProps;

export function ProgramOutlineSection({ title, summary, parts, programKey }: ProgramOutlineSectionProps) {
  const [primaryBlue] = useToken('colors', ['primary.blue']);
  const numberOfCoursesToIncrementBy = 4;
  const [numberOfCoursesToShow, setNumberOfCoursesToShow] = useState(numberOfCoursesToIncrementBy);
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';

  // Sort all optional parts to the end of the list
  const sortedCourses = useMemo(() => {
    return parts.sort((a, b) => {
      if (a.isOptional && !b.isOptional) return 1;
      if (!a.isOptional && b.isOptional) return -1;
      return 0;
    });
  }, [parts]);

  const numberOfCoursesLeft = sortedCourses.length - numberOfCoursesToShow;
  const buttonLabel = useMemo(() => {
    if (numberOfCoursesLeft <= 0) return 'See Fewer Courses';
    return `See ${numberOfCoursesLeft} More Course${numberOfCoursesLeft > 1 ? 's' : ''}`;
  }, [numberOfCoursesLeft]);

  function handleSeeMore() {
    if (isExperimentalTrackEventsEnabled) {
      trackEvent({
        name: 'Product Page CTA Clicked',
        type: 'click',
        label: 'more_courses_clicked',
        additionalAttributes: {
          product_ids: [programKey],
        },
      });
    }
    setNumberOfCoursesToShow((prev) => prev + numberOfCoursesToIncrementBy);
  }

  const hasMultipleParts = parts.length > 1;

  return (
    <Box as='section'>
      <ContentContainer display='flex' flexDirection='column' gap={6}>
        <Flex flexWrap='wrap' gap={2} justifyContent='space-between'>
          <Heading
            as='h3'
            size={{
              base: 'heading-6',
              md: 'heading-5',
            }}
          >
            {title}
          </Heading>
          <ProgramStats noOfCourses={summary.courses} noOfLessons={summary.lessons} noOfProjects={summary.projects} />
        </Flex>

        <Accordion
          allowMultiple={hasMultipleParts}
          allowToggle={hasMultipleParts}
          borderRadius='8px'
          defaultIndex={[0]}
          orientation='vertical'
          sx={{
            '> div + div': {
              marginTop: '16px',
            },
          }}
        >
          {sortedCourses.map((part, idx) => (
            <AccordionItem key={part.key} hidden={idx >= numberOfCoursesToShow}>
              {({ isExpanded }) => (
                <>
                  <AccordionButton
                    borderBottom='1px solid !important'
                    borderBottomColor='supportBlue.20 !important'
                    borderLeftColor='supportGray.20 !important'
                    borderRadius='8px'
                    borderRightColor='supportGray.20 !important'
                    borderTopColor='supportGray.20 !important'
                    borderTopWidth='1px'
                    gap={2}
                    _expanded={{
                      borderTopColor: 'primary.blue !important',
                      borderLeftColor: 'primary.blue !important',
                      borderRightColor: 'primary.blue !important',
                      borderTopRightRadius: '8px',
                      borderTopLeftRadius: '8px',
                      borderBottomRightRadius: '0px',
                      borderBottomLeftRadius: '0px',
                    }}
                    _hover={{
                      backgroundColor: 'supportGray.10',
                    }}
                    onClick={() => {
                      if (isExperimentalTrackEventsEnabled && !isExpanded) {
                        trackEvent({
                          name: 'Product Page CTA Clicked',
                          type: 'click',
                          label: 'expand_collapse_clicked',
                        });
                      }
                    }}
                  >
                    <Heading
                      as='span'
                      display='inline-flex'
                      flex='1'
                      gap={2}
                      size='heading-7'
                      textAlign='start'
                      alignItems={{
                        md: 'center',
                      }}
                      flexDirection={{
                        base: 'column',
                        md: 'row',
                      }}
                    >
                      {hasMultipleParts && (
                        <Box as='span' color='supportGray.60' fontSize='sm' whiteSpace='nowrap'>
                          Course {idx + 1}:{' '}
                          {part.isOptional && (
                            <Tag
                              alignSelf='flex-start'
                              padding={1}
                              size='xs'
                              display={{
                                base: 'inline-flex',
                                md: 'none',
                              }}
                            >
                              <TagLabel fontSize='xs' lineHeight='xs'>
                                Optional
                              </TagLabel>
                            </Tag>
                          )}
                        </Box>
                      )}
                      <Box
                        as='span'
                        noOfLines={isExpanded ? undefined : 1}
                        wordBreak={isExpanded ? undefined : 'break-all'}
                      >
                        {part.title}
                      </Box>{' '}
                      {hasMultipleParts && part.isOptional && (
                        <Tag
                          alignSelf='flex-start'
                          padding={1}
                          size='xs'
                          display={{
                            base: 'none',
                            md: 'inline-flex',
                          }}
                        >
                          <TagLabel fontSize='xs' lineHeight='xs'>
                            Optional
                          </TagLabel>
                        </Tag>
                      )}
                    </Heading>
                    {hasMultipleParts && (
                      <>
                        {isExpanded ? (
                          <ChevronUpIcon color='primary.blue' height={ICON_SIZE} width={ICON_SIZE} />
                        ) : (
                          <ChevronDownIcon color='primary.blue' height={ICON_SIZE} width={ICON_SIZE} />
                        )}
                      </>
                    )}
                  </AccordionButton>
                  <AccordionPanel
                    bgColor='supportGray.10'
                    borderBottom={`1px solid ${primaryBlue} !important`}
                    borderBottomLeftRadius='8px'
                    borderBottomRightRadius='8px'
                    borderColor='primary.blue !important'
                  >
                    <CourseContent
                      hasMultipleParts={hasMultipleParts}
                      lessons={part.lessons}
                      rawDurationInMs={part.rawDurationInMs}
                      summary={part.summary}
                    />
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          ))}
        </Accordion>

        {numberOfCoursesLeft > 0 && (
          <Button display='block' variant='outline' onClick={handleSeeMore}>
            {buttonLabel}
          </Button>
        )}
      </ContentContainer>
    </Box>
  );
}

function ProgramStats({
  noOfCourses,
  noOfLessons,
  noOfProjects,
}: {
  /**
   * Nanodegree programs have courses but courses _only_ have lessons.
   */
  noOfCourses?: number;
  noOfLessons: number;
  noOfProjects: number;
}) {
  return (
    <List
      alignItems='center'
      display='flex'
      flexWrap='wrap'
      _empty={{
        display: 'none',
      }}
      gap={{
        base: 4,
        md: 8,
      }}
    >
      {!!noOfCourses && noOfCourses > 0 && (
        <ListItem whiteSpace='nowrap'>
          <ListIcon as={BooksIcon} color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          {pluralize(noOfCourses, 'course', 'courses')}
        </ListItem>
      )}
      {noOfLessons > 0 && (
        <ListItem whiteSpace='nowrap'>
          <ListIcon as={BookmarkIcon} color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          {pluralize(noOfLessons, 'lesson', 'lessons')}
        </ListItem>
      )}
      {noOfProjects > 0 && (
        <ListItem whiteSpace='nowrap'>
          <ListIcon as={OpenBookIcon} color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          {pluralize(noOfProjects, 'project', 'projects')}
        </ListItem>
      )}
    </List>
  );
}

function CourseContent({
  hasMultipleParts,
  summary,
  rawDurationInMs,
  lessons,
}: {
  hasMultipleParts: boolean;
  summary: string | null;
  rawDurationInMs: number | null;
  lessons: { key: string; title: string; summary: string | null; isProjectLesson: boolean }[];
}) {
  const courseRawDuration = useHumanizeDuration(rawDurationInMs, RAW_DURATION_OPTIONS);
  return (
    <Flex flexDirection='column' gap={6}>
      {hasMultipleParts && (
        <Flex
          flexWrap='wrap-reverse'
          gap={{
            base: 2,
            md: 12,
          }}
        >
          <Text
            flex={{
              md: 1,
            }}
          >
            {summary}
          </Text>
          {!!courseRawDuration && (
            <Tag alignSelf={{ md: 'flex-end' }} borderRadius='12px' flexShrink={0} gap={2} marginLeft='auto' size='sm'>
              <TagLabel>{courseRawDuration}</TagLabel>
              <TagIcon as={StopWatchIcon} color='supportGray.50' height='16px' width='16px' />
            </Tag>
          )}
        </Flex>
      )}
      <List as='ol'>
        {lessons.map((lesson) => (
          <ListItem
            key={lesson.key}
            borderColor='supportBlue.20 !important'
            borderTop='1px solid'
            display='flex'
            paddingY={3}
            _before={{
              content: lesson.isProjectLesson ? `"P"` : `"L" counter(lesson)`,
              bgColor: lesson.isProjectLesson ? 'primary.orange' : 'primary.blue',
              color: 'white',
              fontWeight: '600',
              fontSize: 'xs',
              lineHeight: 'xs',
              marginRight: '12px',
              alignSelf: 'flex-start',
              padding: '1px',
              textAlign: 'center',
              width: '24px',
              flexShrink: 0,
              flexGrow: 0,
              borderRadius: '12px',
              position: 'relative',
              top: '2px',
            }}
            flexDirection={{
              base: 'column',
              md: 'row',
            }}
            gap={{
              base: 1,
              md: 4,
            }}
            sx={{
              counterIncrement: 'lesson',
            }}
          >
            <Flex
              as='span'
              flexDirection={{
                base: 'column',
                md: 'row',
              }}
              gap={{
                base: 1,
                md: 4,
              }}
            >
              <Box
                as='span'
                fontSize='sm'
                fontWeight='600'
                flex={{
                  md: '0 0 224px',
                }}
              >
                {lesson.title}
              </Box>
              <Box as='span'>
                {!!lesson.summary && (
                  <Text fontSize='sm' lineHeight='sm'>
                    {lesson.summary}
                  </Text>
                )}
              </Box>
            </Flex>
          </ListItem>
        ))}
      </List>
    </Flex>
  );
}

export function transformStaticProgramOutlineSectionProps({ program }: { program: Program }) {
  const isNanodegree = isNanodegreeProgram(program);
  if (isNanodegree) {
    const parts = program.parts;
    return {
      title: 'Program Outline',
      summary: {
        courses: parts.length,
        lessons: parts.reduce((acc, part) => acc + part.lessons.length, 0),
        projects: parts.reduce((acc, part) => acc + part.lessons.filter((lesson) => lesson.isProjectLesson).length, 0),
      },
      parts: parts.map((part) => ({
        key: part.key,
        title: part.title,
        summary: part.summary,
        isOptional: part.isOptional,
        rawDurationInMs: part.rawDurationInMs,
        lessons: part.lessons.map((lesson) => ({
          key: lesson.key,
          title: lesson.title,
          summary: lesson.summary,
          isProjectLesson: lesson.isProjectLesson,
        })),
      })),
    };
  }

  return {
    title: 'Course Outline',
    summary: {
      lessons: program.lessons.length,
      projects: program.lessons.filter((lesson) => lesson.isProjectLesson).length,
    },
    parts: [
      {
        key: program.key,
        title: program.title,
        summary: program.summary,
        isOptional: false,
        rawDurationInMs: program.rawDurationInMs,
        lessons: program.lessons.map((lesson) => ({
          key: lesson.key,
          title: lesson.title,
          summary: lesson.summary,
          isProjectLesson: lesson.isProjectLesson,
        })),
      },
    ],
    programKey: program.key,
  };
}

export type StaticProgramOutlineSectionProps = ReturnType<typeof transformStaticProgramOutlineSectionProps>;
